'use client';

import { useMemo } from 'react';

import { RECRUIT_SOURCE } from '@highlight-cp/constants';
import {
  EXPERIENCE_TYPES,
  PROJECT_SUBTYPES,
  PROJECT_TYPES,
} from '@highlight-cp/feature-solution-mgmt';
import type { ProjectSubtypeOption, ProjectTypeOption } from '@highlight-cp/feature-solution-mgmt';
import {
  getProjectLogisticsTypes,
  isByolLogisticsType,
  projectApi,
} from '@highlight-cp/project-api';

import { useGetCurrentProject } from '../use-get-current-project';
import isSequentialMonadicSolution from './is-sequential-monadic-solution';

type CopyVariantObject = Record<string, string>;

interface UseProjectTypeOptions<V = CopyVariantObject> {
  copyVariants?: {
    Digital: V;
    SequentialMonadic?: V;
    Physical: V;
    NonStimulus: V;
  };
  overrideProjectId?: number | null;
}

/**
 * ### useProjectType
 *
 * @description
 * Hook to get the project type and copy variants for the current project.
 * If overrideProjectId is provided, it will be used to fetch the project instead of using the one from the Redux store.
 *
 * `isSequentialMonadic` is derived from the project's selected solution name.
 *
 * @example
 * // without copy variants
 * const { isLegacyExperience, isDigital, isNonStimulus, solution, type, isLoading, customerAppType } = useProjectType();
 *
 * // with copy variants
 * const { copy } = useProjectType<CopyVariantsType>({ copyVariants });
 *
 * // with override project ID
 * const { isDigital, isNonStimulus } = useProjectType({ overrideProjectId: 123 });
 *
 * @param options - UseProjectTypeOptions
 * @returns UseProjectTypeResult
 */
const useProjectType = <V = CopyVariantObject>(options: UseProjectTypeOptions<V> = {}) => {
  const { copyVariants, overrideProjectId } = options;
  const { data: reduxProject, isLoading: isLoadingReduxProject = true } = useGetCurrentProject();
  const { data: overrideProject, isLoading: isLoadingOverrideProject } =
    projectApi.useGetProjectsByProjectIdQuery(
      { projectId: overrideProjectId ?? 0 },
      { skip: (overrideProjectId ?? 0) <= 0 },
    );
  const { data: solutions = [], isLoading: isLoadingSolutions } = projectApi.useGetSolutionsQuery(
    {},
  );

  const project = overrideProjectId ? overrideProject : reduxProject;
  const isLoadingProject = overrideProjectId ? isLoadingOverrideProject : isLoadingReduxProject;

  // Get presentation rules for hybrid detection
  const { data: presentationRules = [] } =
    projectApi.useGetProjectsByProjectIdPresentationrulesQuery(
      { projectId: project?.id ?? 0 },
      { skip: !project?.id },
    );

  const projectType = project?.type as ProjectTypeOption | undefined;
  const projectSubtype = project?.subtype as ProjectSubtypeOption | undefined;
  const logisticsTypes = getProjectLogisticsTypes(project?.logisticsType);

  const isDigital =
    window?.location?.pathname?.includes('/stimuli') ||
    projectType === PROJECT_TYPES.Digital ||
    projectType === PROJECT_TYPES.ConceptTest;

  const isNonStimulus = projectSubtype === PROJECT_SUBTYPES.NonStimulus;
  const isPhysical = projectType === PROJECT_TYPES.Physical;
  const isByol = isByolLogisticsType(logisticsTypes);

  const isByoc = project?.recruitSource === RECRUIT_SOURCE.CUSTOM_COMMUNITY;

  const isGuestLinkOnly = project?.recruitSource === RECRUIT_SOURCE.GUEST_LINK;

  const solution = useMemo(
    () => solutions?.find(({ id }) => id === project?.solutionId),
    [solutions, project?.solutionId],
  );

  const isSequentialMonadic = useMemo(() => isSequentialMonadicSolution(solution), [solution]);

  const isLegacyExperience = useMemo(
    () => Boolean(!solution || solution?.experienceType === EXPERIENCE_TYPES.Old),
    [solution],
  );

  const customerAppType = useMemo(() => {
    if (solution?.experienceType === EXPERIENCE_TYPES.New) {
      return 'Platform';
    }

    const isHybrid = presentationRules.some((rule) => {
      return (
        (rule.slug === 'penalty_analysis_tab' || rule.slug === 'alienation_analysis_tab') &&
        rule.visible?.value === true
      );
    });

    return isHybrid ? 'Hybrid' : 'Legacy';
  }, [solution, presentationRules]);

  const copy = useMemo(() => {
    if (!copyVariants) return {} as V;
    if (isNonStimulus) return copyVariants.NonStimulus;
    if (!isDigital) return copyVariants.Physical;
    if (isSequentialMonadic) {
      return copyVariants.SequentialMonadic ?? copyVariants.Digital;
    }
    return copyVariants.Digital;
  }, [copyVariants, isDigital, isNonStimulus, isSequentialMonadic]);

  return {
    copy,
    customerAppType,
    isByoc,
    isByol,
    isDigital,
    isGuestLinkOnly,
    isLegacyExperience,
    isLoading:
      isLoadingProject ||
      isLoadingSolutions ||
      (overrideProjectId ? !overrideProject : !reduxProject),
    isSequentialMonadic,
    isNewExperienceDigital: isDigital && !isLegacyExperience,
    isNonStimulus,
    isPhysical,
    shouldChainScreenerToSurveys: isDigital && !isLegacyExperience,
    solution,
    subtype: projectSubtype,
    type: projectType,
  };
};

export default useProjectType;
